// Utility to convert the RX preview into a multi-page PDF that preserves
// the on-screen layout (header, signature, pagination, template background,
// referral page, lab attachment pages, etc.).
//
// Strategy:
//   1. Find every `.rx-paper` element under the given root (each one
//      represents a real print page).
//   2. Rasterize each page individually with html2canvas at a high pixel
//      ratio so fonts stay crisp.
//   3. Stitch each page into a jsPDF document at its actual print size
//      (A4 / Letter / Half), one PDF page per `.rx-paper` element.
//
// We intentionally use the element's own bounding box (px), and tell jsPDF
// the page is exactly that many pixels wide/tall, so there is zero scaling
// distortion vs. the on-screen preview.

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

function sanitizeFilename(s) {
  return String(s || "")
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/\s+/g, "_")
    .slice(0, 80) || "RX";
}

export function buildRxFilename(prescription) {
  const num = prescription?.prescription_number || prescription?.id || "RX";
  const patient = prescription?.patient_snapshot?.full_name || "Patient";
  return `RX-${sanitizeFilename(num)}-${sanitizeFilename(patient)}.pdf`;
}

export async function downloadRxAsPdf({
  root,
  filename = "RX.pdf",
  onProgress,
}) {
  if (!root) throw new Error("Missing root element to render");

  // The page picker greys out sheets the user excluded; honour that here too so
  // the PDF matches what Print would produce.
  const pages = Array.from(root.querySelectorAll(".rx-paper")).filter(
    (el) => !el.classList.contains("rx-deselected")
  );
  if (pages.length === 0) throw new Error("Nothing to export");

  // Use the first page to decide PDF page units; we'll set each page's size
  // individually to match its actual rendered pixel box, so it doesn't matter
  // what we initialize with.
  const pdf = new jsPDF({
    unit: "px",
    format: [pages[0].offsetWidth, pages[0].offsetHeight],
    orientation: pages[0].offsetWidth > pages[0].offsetHeight ? "l" : "p",
    compress: true,
  });

  for (let i = 0; i < pages.length; i++) {
    const node = pages[i];
    if (onProgress) onProgress({ current: i + 1, total: pages.length });

    const w = node.offsetWidth;
    const h = node.offsetHeight;

    // Render the page at 2x for crisp output.
    const canvas = await html2canvas(node, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      windowWidth: w,
      windowHeight: h,
      // Force the captured area to the actual page box (so any shadows
      // / external paddings from the wrapper get cropped out).
      width: w,
      height: h,
      x: 0,
      y: 0,
    });

    const imgData = canvas.toDataURL("image/jpeg", 0.95);

    if (i === 0) {
      pdf.deletePage(1);
    }
    pdf.addPage([w, h], w > h ? "l" : "p");
    pdf.addImage(imgData, "JPEG", 0, 0, w, h, undefined, "FAST");
  }

  pdf.save(filename);
}
